import React, {useEffect, useRef, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

// Icons
import IconProfile from "../../Assets/SVGs/Icons/UI/Profile";
import IconGear from "../../Assets/SVGs/Icons/UI/Gear";
import IconLifeRing from "../../Assets/SVGs/Icons/AlertAndInfo/LifeRing";
import IconExit from "../../Assets/SVGs/Icons/UI/Exit";

// New auth models
import type {AuthUser} from "../../Models/DTOs/AuthUser";

// Routes
import {COMMENT_REPORTING_UI_ENABLED} from "../../Constants/FeatureFlags";
import {RoutePaths} from "../../Constants/RoutePaths";

type Props = {
    authUser?: AuthUser | null;
    logOut: () => void;
    tokenExists: boolean;
};

const AccountDropDown: React.FC<Props> = ({authUser, logOut, tokenExists}) => {
    const
        navigate = useNavigate(),
        {t} = useTranslation(),

        [isOpen, setIsOpen] = useState(false),
        dropdownRef = useRef<HTMLDivElement>(null),

        handleLogOut = () => {
            logOut();
            setTimeout(() => navigate("/"), 500);
        },

        handleToggleDropDown = () => setIsOpen((prev) => !prev),
        handleLoginRedirect = () => navigate(RoutePaths.Login),

        initial =
            (authUser?.first_name?.trim()?.[0] ||
                authUser?.email?.trim()?.[0] ||
                "")?.toUpperCase(),

        renderButtonInner = () => (
            <div className="account-dropdown__button" onClick={handleToggleDropDown}>
                <span>{tokenExists ? t("header-ui-account-button-label") : t("header-ui-sign-in-button-label")}</span>

                {/* Profile icon and initial */}
                <div className="account-dropdown__icon">
                    {IconProfile()}
                    {initial && <span>{initial}</span>}
                </div>
            </div>
        ),

        renderDropDown = () => (
            <div
                onClick={handleToggleDropDown}
                className={`account-dropdown__dropdown ${isOpen ? "is-open" : "is-closed"}`}
            >
                <ul>
                    {!!authUser?.id && (
                        <>
                            <li>
                                <Link to={RoutePaths.Account}>
                                    <>{IconGear()} Settings</>
                                </Link>
                            </li>
                            <li>
                                <Link to={RoutePaths.HelpCentre}>
                                    <>{IconLifeRing()} Help Centre</>
                                </Link>
                            </li>
                            {COMMENT_REPORTING_UI_ENABLED &&
                                (authUser.admin_role_id ?? 0) >= 95 && (
                                    <li>
                                        <Link to={RoutePaths.CommentModeration}>
                                            Comment moderation
                                        </Link>
                                    </li>
                                )}
                        </>
                    )}

                    <li>
                        <button onClick={handleLogOut}>
                            <>{IconExit()} Log Out</>
                        </button>
                    </li>
                </ul>
            </div>
        );

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={dropdownRef}>
            {tokenExists ? (
                <div className={`account-dropdown ${tokenExists ? 'is-authenticated' : 'is-not-authenticated'}`}>
                    {renderButtonInner()}
                    {renderDropDown()}
                </div>
            ) : (
                <div className={`account-dropdown ${tokenExists ? 'is-authenticated' : 'is-not-authenticated'}`} onClick={handleLoginRedirect}>
                    {renderButtonInner()}
                    {renderDropDown()}
                </div>
            )}
        </div>
    );
};

export default AccountDropDown;
